const EXTENSION_STORAGE_KEY = "extensionStorage";
const BUY_AGAIN_TOKEN_SUFFIX = "buy-again-token";
const FALLBACK_TOKEN_KEY = "buy-again-token";
const STORED_SHOPIFY_CUSTOMER_ID_KEY = "buy-again-shopify-customer-id";
const STORED_EMAIL_KEY = "buy-again-email";
const DISCOUNT_CODE_STORAGE_PREFIX = "buy-again-discount-code";

/**
 * The `extensionStorage` blob written by the Shopify theme extension, or null
 * when it is absent or unparseable.
 */
function readExtensionStorage(): Record<string, string> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(EXTENSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, string>;
  } catch {
    return null;
  }
}

function readFromExtensionStorage(suffix: string): string | null {
  if (typeof window === "undefined") return null;
  const data = readExtensionStorage();
  if (!data) return null;
  const key = Object.keys(data).find((k) => k.includes(suffix));
  if (!key) return null;
  const value = data[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function getTokenFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get("so-token");
  return typeof token === "string" && token.length > 0 ? token : null;
}

export function getDiscountCodeFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("discount-code");
  return typeof code === "string" && code.length > 0 ? code : null;
}

function getCurrentUserStorageKey(): string | null {
  if (typeof window === "undefined") return null;
  const winId = (
    window as unknown as { meta?: { page?: { customerId?: number } } }
  )?.meta?.page?.customerId;
  if (winId != null && Number.isFinite(Number(winId))) return `id:${winId}`;
  try {
    const raw = window.localStorage.getItem(STORED_SHOPIFY_CUSTOMER_ID_KEY);
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n)) return `id:${n}`;
    }
  } catch {}
  const token = getStoredToken();
  if (token) return `token:${token}`;
  return null;
}

export function getDiscountCode(): string | null {
  const fromUrl = getDiscountCodeFromUrl();
  if (fromUrl) {
    const userKey = getCurrentUserStorageKey();
    if (userKey) {
      try {
        window.localStorage.setItem(
          `${DISCOUNT_CODE_STORAGE_PREFIX}:${userKey}`,
          fromUrl,
        );
      } catch {}
    }
    return fromUrl;
  }
  if (typeof window === "undefined") return null;
  const userKey = getCurrentUserStorageKey();
  if (!userKey) return null;
  try {
    const stored = window.localStorage.getItem(
      `${DISCOUNT_CODE_STORAGE_PREFIX}:${userKey}`,
    );
    return typeof stored === "string" && stored.length > 0 ? stored : null;
  } catch {
    return null;
  }
}

export function storeToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FALLBACK_TOKEN_KEY, token);
    removeTokenFromUrl();
  } catch {
  }
}

export function removeTokenFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.has("so-token")) {
    url.searchParams.delete("so-token");
    const clean = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, "", clean);
  }
}

export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const fromExtension = readFromExtensionStorage(BUY_AGAIN_TOKEN_SUFFIX);
    if (fromExtension) {
      window.localStorage.setItem(FALLBACK_TOKEN_KEY, fromExtension);
      return fromExtension;
    }
    const fromFallback = window.localStorage.getItem(FALLBACK_TOKEN_KEY);
    return typeof fromFallback === "string" && fromFallback.length > 0
      ? fromFallback
      : null;
  } catch {
    return null;
  }
}

/**
 * True when the storefront itself has the customer logged in.
 *
 * Reads `window.meta.page.customerId` only — deliberately not the cached copy
 * in local storage, which outlives a storefront logout. Callers use this to
 * decide whether logging out has to go through Shopify.
 */
export function isShopifyLoggedIn(): boolean {
  if (typeof window === "undefined") return false;
  const id = (
    window as unknown as { meta?: { page?: { customerId?: number } } }
  )?.meta?.page?.customerId;
  return id != null && Number.isFinite(Number(id));
}

/** Remember the address a sign-in code was verified against. */
export function storeEmail(email: string): void {
  if (typeof window === "undefined") return;
  const normalized = email.trim().toLowerCase();
  if (!normalized) return;
  try {
    window.localStorage.setItem(STORED_EMAIL_KEY, normalized);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * The address the customer signed in with, for naming the account in the UI.
 * Survives in token mode, where the backend has no customer to report back.
 */
export function getStoredEmail(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(STORED_EMAIL_KEY);
    return typeof stored === "string" && stored.length > 0 ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Forget everything that identifies the current customer to the widget.
 *
 * Every identity source in `getCustomerIdentifier` has to go, or the next read
 * signs them straight back in:
 * - `buy-again-token`, and the copy inside the `extensionStorage` blob that
 *   `getStoredToken` re-hydrates the fallback key from;
 * - the cached Shopify customer id, which is the lowest-priority identifier and
 *   would otherwise resurrect the previous account;
 * - the stored email and the discount code held against the outgoing customer.
 *
 * Local cart state needs no explicit clearing — it is keyed by
 * `getCartIdentityFingerprint`, so it invalidates itself once the identity is
 * gone. Does not touch the Shopify session; see `isShopifyLoggedIn`.
 */
export function clearStoredSession(): void {
  if (typeof window === "undefined") return;

  // Read the outgoing user key before removing what it is derived from.
  const userKey = getCurrentUserStorageKey();

  try {
    const extensionData = readExtensionStorage();
    if (extensionData) {
      const keys = Object.keys(extensionData).filter((k) =>
        k.includes(BUY_AGAIN_TOKEN_SUFFIX),
      );
      if (keys.length > 0) {
        for (const key of keys) delete extensionData[key];
        window.localStorage.setItem(
          EXTENSION_STORAGE_KEY,
          JSON.stringify(extensionData),
        );
      }
    }
  } catch {
    /* best effort: the fallback key below is what getStoredToken reads first */
  }

  try {
    window.localStorage.removeItem(FALLBACK_TOKEN_KEY);
    window.localStorage.removeItem(STORED_SHOPIFY_CUSTOMER_ID_KEY);
    window.localStorage.removeItem(STORED_EMAIL_KEY);
    if (userKey) {
      window.localStorage.removeItem(
        `${DISCOUNT_CODE_STORAGE_PREFIX}:${userKey}`,
      );
    }
  } catch {
    /* ignore private mode */
  }
}

const LOGOUT_PENDING_KEY = "so_widget_logout_pending";

/**
 * Remember that we are about to hand the customer to Shopify's logout.
 *
 * `/account/logout` takes no redirect parameter — the Liquid docs are explicit
 * that it always lands on the homepage — so the panel would otherwise vanish
 * mid-flow, right when the customer is trying to sign in as someone else.
 * Session-scoped: the flag has to survive one navigation and no more.
 */
export function markLogoutRedirectPending(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(LOGOUT_PENDING_KEY, "1");
  } catch {
    /* private mode: the panel simply will not reopen */
  }
}

/** Reads the marker and clears it, so the panel reopens exactly once. */
export function consumeLogoutRedirectPending(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const pending = window.sessionStorage.getItem(LOGOUT_PENDING_KEY) === "1";
    if (pending) window.sessionStorage.removeItem(LOGOUT_PENDING_KEY);
    return pending;
  } catch {
    return false;
  }
}
